import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import useApi from '../utils/api';
import { useGameSpace } from './GameSpaceContext';

const GameContext = createContext();

export const useGame = () => useContext(GameContext);

export const GameProvider = ({ children }) => {
    const { get } = useApi();
    const { selectedSpaceId } = useGameSpace();
    
    /*  
          currentSession: {
            'session_id': self.session_id,
            'user_to_session': self.user_to_session,
            'game_in_session': self.game_in_session,
            'host_session_id': self.host_session_id,
            'session_code': self.session_code,
            'is_multiplayer': self.is_multiplayer,
            'is_host': self.is_host,
            'is_participating': self.is_participating,
            'current_chapter': self.current_chapter,
            'chapter_score_released_host': self.chapter_score_released_host,
            'chapter_scores_released': self.chapter_scores_released, # Not used in the frontend
            'start_time': self.start_time,
            'end_time': self.end_time,
            'total_score': self.total_score,
            'completed': self.completed,
            'feedback': self.feedback
        }
    
    */
    const [currentSession, setCurrentSessionState] = useState(() => {
        if (typeof window === 'undefined') return null;
        const stored = window.localStorage.getItem('currentSession');

        if (!stored) return null;
        try {
            return JSON.parse(stored);
        } catch (err) {
            console.error('Invalid session in localStorage:', err);
            window.localStorage.removeItem('currentSession');
            return null;
        }

    });
    const [sessionLastUpdated, setSessionLastUpdated] = useState(null); // State to store the last updated session, initially set to null
    const isUpdatingSession = useRef(false); // Flag to prevent storage events from clearing session during updates
    
    // Players state for multiplayer sessions
    const [players, setPlayers] = useState([]);
    const [playersLoading, setPlayersLoading] = useState(false);
    
    const setCurrentSession = (sessionData) => {
        // Clear flag at start of new update
        if (isUpdatingSession.current) {
            isUpdatingSession.current = false;
        }
        
        // Set flag to prevent storage events from clearing session during update
        isUpdatingSession.current = true;
        
        setCurrentSessionState(sessionData);
        setSessionLastUpdated(new Date());
        if (typeof window !== 'undefined') {
            if (sessionData) {
                window.localStorage.setItem('currentSession', JSON.stringify(sessionData));
            } else {
                window.localStorage.removeItem('currentSession');
            }
        }
        
        // Don't clear flag automatically - let it persist until next update
        // This prevents race conditions with storage events
    }

    // Fetch players for multiplayer sessions
    const fetchPlayers = useCallback(async () => {
        if (!currentSession?.session_id || !currentSession?.is_multiplayer) {
            setPlayers([]);
            return;
        }
        
        setPlayersLoading(true);
        try {
            const data = await get(`${process.env.REACT_APP_API_URL}/api/sessions/${currentSession.session_id}/players/user/${currentSession.user_to_session}`, selectedSpaceId);
            
            const playersArray = Array.isArray(data) ? data : [];
            setPlayers(playersArray);
            
        } catch (error) {
            console.error('Failed to fetch players:', error);
            setPlayers([]);
        } finally {
            setPlayersLoading(false);
        }
    }, [get, selectedSpaceId, currentSession?.session_id, currentSession?.is_multiplayer, currentSession?.user_to_session]);

    // Fetch players when session changes
    useEffect(() => {
        if (currentSession?.is_multiplayer) {
            fetchPlayers();
        } else {
            setPlayers([]);
        }
    }, [currentSession?.session_id, currentSession?.is_multiplayer, fetchPlayers]);

    const shouldRefetchSession = (thresholdSeconds = 5) => {
        if (!sessionLastUpdated) return true;
        const now = Date.now();
        return (now - sessionLastUpdated.getTime()) / 1000 > thresholdSeconds; //More than 10 seconds here.
      };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleStorage = (event) => {
            if (event.key === 'currentSession') {
                if (!event.newValue) {
                    // Don't clear session if we're in the middle of updating it
                    if (isUpdatingSession.current) {
                        return;
                    }
                    
                    // Also check if this is a legitimate clear (not from our own update)
                    const currentStored = window.localStorage.getItem('currentSession');
                    if (currentStored) {
                        return;
                    }
                    
                    setCurrentSessionState(null);
                    setSessionLastUpdated(new Date());
                    return;
                }
                try {
                    const stored = JSON.parse(event.newValue);
                    setCurrentSessionState(stored);
                    setSessionLastUpdated(new Date());
                } catch (err) {
                    console.error('Invalid session in localStorage:', err);
                    window.localStorage.removeItem('currentSession');
                    setCurrentSessionState(null);
                    setSessionLastUpdated(new Date());
                }

            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);
      

    const value = {
        currentSession,
        setCurrentSession, // Provide the debug version through context
        sessionLastUpdated,
        shouldRefetchSession,
        players,
        setPlayers,
        playersLoading,
        fetchPlayers
    };

    return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
